import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import TvIcon from "@mui/icons-material/Tv";
import HotelIcon from "@mui/icons-material/Hotel";
import ViewQuiltIcon from "@mui/icons-material/ViewQuilt";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import DevicesIcon from "@mui/icons-material/DevicesOther";
import KeyIcon from "@mui/icons-material/KeyOutlined";
import DashboardIcon from "@mui/icons-material/DashboardOutlined";
import RoomServiceIcon from "@mui/icons-material/RoomServiceOutlined";
import SettingsIcon from "@mui/icons-material/SettingsOutlined";
import TuneIcon from "@mui/icons-material/TuneOutlined";
import ScheduleIcon from "@mui/icons-material/ScheduleOutlined";
import BoltIcon from "@mui/icons-material/BoltOutlined";
import BadgeIcon from "@mui/icons-material/BadgeOutlined";
import InsightsIcon from "@mui/icons-material/InsightsOutlined";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafetyOutlined";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphoneOutlined";
import VideoCallIcon from "@mui/icons-material/VideoCallOutlined";
import type { SchemeKnob, SchemeKnobValues } from "@shared/types/foyer";

export type PremiseSection =
  | "overview"
  | "rooms"
  | "codes"
  | "devices"
  | "energy"
  | "engagement"
  | "template"
  | "media"
  | "pms"
  | "config"
  | "schedule"
  | "residents"
  | "video-calls"
  | "guest";

/** Sub-sections of the Template editor, shown nested under "Template". */
export type TemplateSubSection =
  | "picker"
  | "design"
  | "typography"
  | "knobs"
  | "branding"
  | "background"
  | "apps"
  | "radio"
  | "info"
  | "menu"
  | "vilmer"
  | "slots"
  | "wifi"
  | "welcome"
  | "relatives"
  | "instagram"
  | "weather"
  | "custom"
  | "icons";

/** Top-level collapsible sections inside the Template editor. */
type TemplateSection = "components" | "styling";

/** The Template editor follows the sales flow top-to-bottom: pick a template,
 *  fill in its on-screen Components, then tune the Styling. `section` puts each
 *  tab under one of the two collapsible groups; only "Pick template" is flat. */
export const TEMPLATE_SUBS: {
  key: TemplateSubSection;
  section?: TemplateSection;
}[] = [
  { key: "picker" },
  { key: "knobs", section: "components" },
  { key: "apps", section: "components" },
  { key: "radio", section: "components" },
  { key: "info", section: "components" },
  { key: "slots", section: "components" },
  { key: "menu", section: "components" },
  { key: "vilmer", section: "components" },
  { key: "wifi", section: "components" },
  { key: "welcome", section: "components" },
  { key: "instagram", section: "components" },
  { key: "weather", section: "components" },
  { key: "relatives", section: "components" },
  // The operator's own screen. "Custom" is only its fallback name - the label
  // shown in the nav is whatever they titled it (see `templateSubLabel`).
  { key: "custom", section: "components" },
  { key: "design", section: "styling" },
  { key: "typography", section: "styling" },
  { key: "background", section: "styling" },
  { key: "branding", section: "styling" },
  { key: "icons", section: "styling" },
];

/**
 * Existence + on/off gate for the Component tabs, shared with the root nav.
 *
 * Existence: does this template even declare the screen at all. On/off: is
 * its controlling knob currently switched off - a knob-controlled tab is
 * dropped from the nav entirely while it's off (2026-08-03: an off feature no
 * longer leaves a stub behind). `templateKnobs`/`knobValues` are optional so
 * callers that only care about existence (or haven't loaded a scheme yet) can
 * omit them and get the old existence-only behaviour.
 */
export function visibleTemplateSubs(opts: {
  templateScreens?: string[];
  templateHasSlots?: boolean;
  templateKnobs?: SchemeKnob[];
  knobValues?: SchemeKnobValues;
}) {
  const isCare = opts.templateScreens?.includes("tv") ?? false;
  return TEMPLATE_SUBS.filter((s) => {
    if (s.key === "icons") return isCare;
    if (s.key === "slots") return opts.templateHasSlots ?? false;
    if (TEMPLATE_SUBS_GATED.has(s.key))
      return opts.templateScreens?.includes(s.key) ?? false;
    return true;
  }).filter((s) => {
    const toggleId = SUB_TOGGLE[s.key];
    if (!toggleId) return true;
    return toggleValue(toggleId, opts.templateKnobs, opts.knobValues) !== false;
  });
}

/** Component tabs shown only when the selected template declares that screen
 *  (so e.g. care doesn't show a Wi-Fi tab it can't render). */
const TEMPLATE_SUBS_GATED = new Set<TemplateSubSection>([
  "menu",
  "vilmer",
  "wifi",
  "welcome",
  "relatives",
  "instagram",
  "weather",
  "custom",
]);

/**
 * Maps a Component tab to the toggle knob that controls it.
 *
 * The tab is dropped from the nav entirely while that toggle is off (see
 * `visibleTemplateSubs`/`subIsOff`) - turning a feature off removes it from
 * the menu instead of leaving a muted stub behind. The always-reachable
 * landing spot is the Screens board itself (Components → Screens, `"knobs"`),
 * which is absent from this map and switches everything back on. Tabs absent
 * from this map (Pick template, Screens itself) have no on/off state at all.
 */
export const SUB_TOGGLE: Partial<Record<TemplateSubSection, string>> = {
  apps: "showApps",
  radio: "showRadio",
  info: "showInfo",
  menu: "showMenu",
  vilmer: "showVilmer",
  slots: "showPromotions",
  wifi: "showWifi",
  welcome: "showWelcome",
  relatives: "showRelatives",
  instagram: "showInstagram",
  weather: "showWeather",
  custom: "showCustom",
};

/** Effective value of a toggle knob: the stored value, else the manifest
 *  default. Returns null when the template doesn't declare it (→ don't gate). */
export function toggleValue(
  knobId: string,
  manifestKnobs: SchemeKnob[] | undefined,
  knobValues: SchemeKnobValues | undefined
): boolean | null {
  const knob = manifestKnobs?.find(
    (k) => k.id === knobId && k.type === "toggle"
  );
  if (!knob || knob.type !== "toggle") return null;
  const v = knobValues?.[knobId];
  return typeof v === "boolean" ? v : knob.default;
}

/**
 * Effective value of a `text` knob: the operator's, else the manifest default.
 *
 * A blank string resolves to the default rather than to "", on purpose - a
 * screen whose name is empty would render a nameless nav row and a nameless
 * card, and clearing a field is how an operator asks for the name back.
 */
export function textValue(
  knobId: string,
  manifestKnobs: SchemeKnob[] | undefined,
  knobValues: SchemeKnobValues | undefined
): string | null {
  const knob = manifestKnobs?.find((k) => k.id === knobId && k.type === "text");
  if (!knob || knob.type !== "text") return null;
  const v = knobValues?.[knobId];
  return (typeof v === "string" ? v.trim() : "") || knob.default;
}

/**
 * What a Component tab is CALLED for this premise: the title the operator gave
 * the screen when the template lets them name it, else the tab's own label.
 *
 * Generic on purpose - it follows `SUB_TOGGLE` → the toggle's `titleKnobId` →
 * that text knob - so a second renameable screen needs no change here, and the
 * in-editor sidebar, the root nav and the page header can all import one
 * answer instead of each inventing their own.
 */
export function templateSubLabel(
  sub: { key: TemplateSubSection; label: string },
  manifestKnobs: SchemeKnob[] | undefined,
  knobValues: SchemeKnobValues | undefined
): string {
  const toggleId = SUB_TOGGLE[sub.key];
  const toggle = manifestKnobs?.find(
    (k) => k.id === toggleId && k.type === "toggle"
  );
  if (!toggle || toggle.type !== "toggle" || !toggle.titleKnobId)
    return sub.label;
  return textValue(toggle.titleKnobId, manifestKnobs, knobValues) ?? sub.label;
}

interface Props {
  active: PremiseSection;
  templateSub: TemplateSubSection;
  /** Standalone mode only: whether the column is expanded. Ignored when
   *  `embedded` (the drawer decides). */
  open?: boolean;
  onSelect: (section: PremiseSection) => void;
  onSelectTemplateSub: (sub: TemplateSubSection) => void;
  roomCount: number;
  /** Show the Care group (Residents authoring) - care premises only, and only
   *  for users who can manage care profiles. */
  showResidents?: boolean;
  /** Show the PMS Configuration entry. A PMS is a hotel property-management
   *  system: care premises (hospital / senior living) have no PMS to bind, so
   *  the entry is hidden for them. Defaults to shown. */
  showPms?: boolean;
  /** Whether video calling is switched on for this premise (Config → Video
   *  calls). The Video calls nav item is shown only when this is true - a
   *  premise with calling off has no directory or log worth reaching. */
  videoCallsEnabled?: boolean;
  /** Screen ids the selected template's manifest declares (gates component tabs). */
  templateScreens?: string[];
  /** True when the selected template declares feature-slots (gates Promotions). */
  templateHasSlots?: boolean;
  /** The selected template's declared knobs - used to resolve toggle defaults. */
  templateKnobs?: SchemeKnob[];
  /** The premise's stored knob choices - a section tab is dropped from the nav
   *  when its controlling toggle resolves to off. */
  knobValues?: SchemeKnobValues;
  /** Render bare, for hosting inside the root drawer: no fixed width, no
   *  border, no horizontal collapse - the drawer owns all of that. */
  embedded?: boolean;
}

export default function PremiseSidebar({
  active,
  templateSub,
  open = true,
  onSelect,
  onSelectTemplateSub,
  roomCount,
  showResidents,
  showPms = true,
  videoCallsEnabled,
  templateScreens,
  templateHasSlots,
  templateKnobs,
  knobValues,
  embedded = false,
}: Props) {
  const { t } = useTranslation(["premises", "common"]);
  // "Card icons" is Care-only - Care is the one template that declares a "tv"
  // screen (the others use a launch action / "hotel").
  const isCare = templateScreens?.includes("tv") ?? false;
  // Existence gate - does this template even have the surface at all.
  const visibleSubs = TEMPLATE_SUBS.filter((s) => {
    if (s.key === "icons") return isCare;
    if (s.key === "slots") return templateHasSlots ?? false;
    if (TEMPLATE_SUBS_GATED.has(s.key))
      return templateScreens?.includes(s.key) ?? false;
    return true;
  });
  // On/off gate - a knob-controlled tab that's currently off is dropped from
  // the nav rather than shown muted (2026-08-03). The always-reachable Screens
  // board (Components → Screens) has no SUB_TOGGLE entry, so it's never
  // affected and stays the way back on.
  const subIsOff = (key: TemplateSubSection) => {
    const toggleId = SUB_TOGGLE[key];
    return (
      !!toggleId && toggleValue(toggleId, templateKnobs, knobValues) === false
    );
  };
  // A renameable screen answers to the name the operator gave it, here as well
  // as on its own page - a nav row still reading "Custom" after they typed
  // "Spa" is the same tab twice as far as the operator can tell.
  const subLabel = (s: { key: TemplateSubSection }) =>
    templateSubLabel(
      { key: s.key, label: t(`premiseSidebar.templateSubs.${s.key}`) },
      templateKnobs,
      knobValues
    );
  // The Template group is collapsible. Auto-open when its section is active,
  // but let the user collapse it again via the chevron.
  const [tplOpen, setTplOpen] = useState(active === "template");
  useEffect(() => {
    if (active === "template") setTplOpen(true);
  }, [active]);

  // Most tabs are flat ("Pick template", "Components"); the styling tabs sit
  // under a collapsible "Look" group. The group is collapsed on load but
  // auto-opens when the active tab lives inside it.
  // Ungrouped tabs never carry a SUB_TOGGLE entry (only "Pick template" today),
  // so they need no on/off filter.
  const ungroupedSubs = visibleSubs.filter((s) => !s.section);
  const sectionKey = (sec: string) => `section:${sec}`;
  // A section header disappears too once every item under it is off - an
  // empty "Components" group with nothing inside is worse than no group.
  const visibleSections = TEMPLATE_SUBS.map((s) => s.section)
    .filter((s, i, arr): s is TemplateSection => !!s && arr.indexOf(s) === i)
    .filter((sec) =>
      visibleSubs.some((s) => s.section === sec && !subIsOff(s.key))
    );
  const sectionItems = (sec: string) =>
    visibleSubs.filter((s) => s.section === sec && !subIsOff(s.key));

  const activeSub = TEMPLATE_SUBS.find((s) => s.key === templateSub);
  const activeSection = activeSub?.section;

  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    const init = new Set<string>();
    for (const s of TEMPLATE_SUBS)
      if (s.section) init.add(sectionKey(s.section));
    return init;
  });
  const toggle = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const isSectionOpen = (sec: string) =>
    !collapsed.has(sectionKey(sec)) || sec === activeSection;

  const body = (
    <>
      <Box sx={{ p: 2 }}>
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <DashboardIcon fontSize="small" />{" "}
          {t("premiseSidebar.groups.property")}
        </Typography>
        <List dense disablePadding sx={{ mt: 0.5, mb: 2 }}>
          <ListItemButton
            selected={active === "overview"}
            onClick={() => onSelect("overview")}
            data-testid="nav-overview"
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <DashboardIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={t("premiseSidebar.items.overview")}
              primaryTypographyProps={{ variant: "body2" }}
            />
          </ListItemButton>
          <ListItemButton
            selected={active === "config"}
            onClick={() => onSelect("config")}
            data-testid="nav-config"
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <TuneIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={t("premiseSidebar.items.config")}
              primaryTypographyProps={{ variant: "body2" }}
            />
          </ListItemButton>
        </List>
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <TvIcon fontSize="small" /> {t("premiseSidebar.groups.tv")}
        </Typography>
        <List dense disablePadding sx={{ mt: 0.5 }}>
          <ListItemButton
            selected={active === "devices"}
            onClick={() => onSelect("devices")}
            data-testid="nav-devices"
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <DevicesIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={t("premiseSidebar.items.devices")}
              primaryTypographyProps={{ variant: "body2" }}
            />
          </ListItemButton>
          <ListItemButton
            selected={active === "schedule"}
            onClick={() => onSelect("schedule")}
            data-testid="nav-schedule"
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <ScheduleIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={t("premiseSidebar.items.schedule")}
              primaryTypographyProps={{ variant: "body2" }}
            />
          </ListItemButton>
          <ListItemButton
            selected={active === "energy"}
            onClick={() => onSelect("energy")}
            data-testid="nav-energy"
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <BoltIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={t("premiseSidebar.items.energy")}
              primaryTypographyProps={{ variant: "body2" }}
            />
          </ListItemButton>
          <ListItemButton
            selected={active === "engagement"}
            onClick={() => onSelect("engagement")}
            data-testid="nav-engagement"
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <InsightsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={t("premiseSidebar.items.engagement")}
              primaryTypographyProps={{ variant: "body2" }}
            />
          </ListItemButton>
          <ListItemButton
            selected={active === "codes"}
            onClick={() => onSelect("codes")}
            data-testid="nav-codes"
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <KeyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={t("premiseSidebar.items.installationCode")}
              primaryTypographyProps={{ variant: "body2" }}
            />
          </ListItemButton>
        </List>
      </Box>
      <Box sx={{ p: 2, pt: 1 }}>
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <RoomServiceIcon fontSize="small" />{" "}
          {t("premiseSidebar.groups.hospitality")}
        </Typography>
        <List dense disablePadding sx={{ mt: 0.5, mb: 2 }}>
          <ListItemButton
            selected={active === "rooms"}
            onClick={() => onSelect("rooms")}
            data-testid="nav-rooms"
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <HotelIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={t("premiseSidebar.items.unitSetup")}
              secondary={t("premiseSidebar.items.unitCount", {
                count: roomCount,
              })}
              primaryTypographyProps={{ variant: "body2" }}
              secondaryTypographyProps={{ variant: "caption" }}
            />
          </ListItemButton>
          {showPms && (
            <ListItemButton
              selected={active === "pms"}
              onClick={() => onSelect("pms")}
              data-testid="nav-pms"
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={t("premiseSidebar.items.pms")}
                primaryTypographyProps={{ variant: "body2" }}
              />
            </ListItemButton>
          )}
          <ListItemButton
            selected={active === "guest"}
            onClick={() => onSelect("guest")}
            data-testid="nav-guest"
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <PhoneIphoneIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={t("premiseSidebar.items.guestWeb")}
              secondary={t("premiseSidebar.items.guestWebHint")}
              primaryTypographyProps={{ variant: "body2" }}
              secondaryTypographyProps={{ variant: "caption" }}
            />
          </ListItemButton>
        </List>
        {showResidents && (
          <>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <HealthAndSafetyIcon fontSize="small" />{" "}
              {t("premiseSidebar.groups.care")}
            </Typography>
            <List dense disablePadding sx={{ mt: 0.5, mb: 2 }}>
              <ListItemButton
                selected={active === "residents"}
                onClick={() => onSelect("residents")}
                data-testid="nav-residents"
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <BadgeIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={t("premiseSidebar.items.residents")}
                  primaryTypographyProps={{ variant: "body2" }}
                />
              </ListItemButton>
              {videoCallsEnabled === true && (
                <ListItemButton
                  selected={active === "video-calls"}
                  onClick={() => onSelect("video-calls")}
                  data-testid="nav-video-calls"
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <VideoCallIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("premiseSidebar.items.videoCalls")}
                    secondary={t("premiseSidebar.items.videoCallsHint")}
                    primaryTypographyProps={{ variant: "body2" }}
                    secondaryTypographyProps={{ variant: "caption" }}
                  />
                </ListItemButton>
              )}
            </List>
          </>
        )}
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <ViewQuiltIcon fontSize="small" />{" "}
          {t("premiseSidebar.groups.content")}
        </Typography>
        <List dense disablePadding sx={{ mt: 0.5 }}>
          <ListItemButton
            selected={active === "template"}
            onClick={() => {
              // Toggle when already on Template; otherwise open + navigate.
              if (active === "template" && tplOpen) {
                setTplOpen(false);
              } else {
                setTplOpen(true);
                onSelect("template");
              }
            }}
            data-testid="nav-template"
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <ViewQuiltIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={t("premiseSidebar.items.template")}
              primaryTypographyProps={{ variant: "body2" }}
            />
            {tplOpen ? (
              <ExpandLess fontSize="small" />
            ) : (
              <ExpandMore fontSize="small" />
            )}
          </ListItemButton>
          <Collapse in={tplOpen} timeout="auto" unmountOnExit>
            <List dense disablePadding>
              {ungroupedSubs.map((s) => (
                <ListItemButton
                  key={s.key}
                  selected={active === "template" && templateSub === s.key}
                  onClick={() => onSelectTemplateSub(s.key)}
                  data-testid={`nav-tpl-${s.key}`}
                  sx={{ pl: 5 }}
                >
                  <ListItemText
                    primary={subLabel(s)}
                    primaryTypographyProps={{ variant: "body2" }}
                  />
                </ListItemButton>
              ))}
              {visibleSections.map((sec) => (
                <Box key={sec}>
                  <ListItemButton
                    onClick={() => toggle(sectionKey(sec))}
                    data-testid={`nav-tpl-section-${sec}`}
                    sx={{ pl: 5 }}
                  >
                    <ListItemText
                      primary={t(`premiseSidebar.sections.${sec}`)}
                      primaryTypographyProps={{
                        variant: "caption",
                        sx: {
                          fontWeight: "fontWeightSemibold",
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                          color: "text.secondary",
                        },
                      }}
                    />
                    {isSectionOpen(sec) ? (
                      <ExpandLess fontSize="small" />
                    ) : (
                      <ExpandMore fontSize="small" />
                    )}
                  </ListItemButton>
                  <Collapse
                    in={isSectionOpen(sec)}
                    timeout="auto"
                    unmountOnExit
                  >
                    <List dense disablePadding>
                      {sectionItems(sec).map((s) => (
                        <ListItemButton
                          key={s.key}
                          selected={
                            active === "template" && templateSub === s.key
                          }
                          onClick={() => onSelectTemplateSub(s.key)}
                          data-testid={`nav-tpl-${s.key}`}
                          sx={{ pl: 7 }}
                        >
                          <ListItemText
                            primary={subLabel(s)}
                            primaryTypographyProps={{ variant: "body2" }}
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  </Collapse>
                </Box>
              ))}
            </List>
          </Collapse>
          <ListItemButton
            selected={active === "media"}
            onClick={() => onSelect("media")}
            data-testid="nav-media"
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <PhotoLibraryIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={t("premiseSidebar.items.media")}
              secondary={t("premiseSidebar.items.mediaHint")}
              primaryTypographyProps={{ variant: "body2" }}
              secondaryTypographyProps={{ variant: "caption" }}
            />
          </ListItemButton>
        </List>
      </Box>
    </>
  );

  if (embedded) {
    return (
      <Box data-testid="premise-sidebar" sx={{ px: 1 }}>
        {body}
      </Box>
    );
  }
  return (
    <Box
      sx={{
        width: open ? 240 : 0,
        transition: "width 200ms ease",
        overflow: "hidden",
        borderRight: open ? "1px solid" : "none",
        borderColor: "divider",
        bgcolor: "background.paper",
        height: "100%",
        flexShrink: 0,
      }}
      data-testid="premise-sidebar"
    >
      <Collapse in={open} orientation="horizontal" sx={{ height: "100%" }}>
        <Box sx={{ width: 240, height: "100%", overflowY: "auto" }}>{body}</Box>
      </Collapse>
    </Box>
  );
}
